import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { makeAABB, type AABB } from "../engine/collision";
import type { MatLib } from "./materials";
import type { LightRig } from "./lighting";

export type Interactable = {
  id: string;
  pos: THREE.Vector3;
  radius: number;
  label: string;
  done: boolean;
  mesh?: THREE.Object3D;
};

export type CoverPoint = { pos: THREE.Vector3; yaw: number };
export type SpawnPoint = { pos: THREE.Vector3; yaw: number; role: "patrol" | "guard" | "roamer" };

export type LevelData = {
  colliders: AABB[];
  nav: THREE.Vector3[];
  covers: CoverPoint[];
  spawns: SpawnPoint[];
  interacts: Interactable[];
  playerStart: THREE.Vector3;
  extract: THREE.Vector3;
  group: THREE.Group;
  minimap: { x: number; z: number; w: number; d: number; kind: string }[];
};

type Bucket = Map<string, THREE.BufferGeometry[]>;

function addMesh(
  buckets: Bucket,
  key: string,
  geo: THREE.BufferGeometry,
  pos: THREE.Vector3,
  rotY = 0,
  scale?: THREE.Vector3
) {
  const g = geo.clone();
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);
  m.compose(pos, q, scale ?? new THREE.Vector3(1, 1, 1));
  g.applyMatrix4(m);
  const arr = buckets.get(key) ?? [];
  arr.push(g);
  buckets.set(key, arr);
}

function boxAt(
  buckets: Bucket,
  colliders: AABB[],
  minimap: LevelData["minimap"],
  mat: string,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  rotY = 0,
  solid = true
) {
  const geo = new THREE.BoxGeometry(1, 1, 1);
  addMesh(buckets, mat, geo, new THREE.Vector3(x, y, z), rotY, new THREE.Vector3(sx, sy, sz));
  if (solid) {
    if (Math.abs(rotY) < 0.01 || Math.abs(Math.abs(rotY) - Math.PI) < 0.01) {
      colliders.push(makeAABB(x, y, z, sx, sy, sz));
    } else if (Math.abs(Math.abs(rotY) - Math.PI / 2) < 0.01) {
      colliders.push(makeAABB(x, y, z, sz, sy, sx));
    } else {
      const ext = Math.max(sx, sz);
      colliders.push(makeAABB(x, y, z, ext, sy, ext));
    }
    minimap.push({ x, z, w: sx, d: sz, kind: mat });
  }
}

export function buildLevel(mats: MatLib, lights: LightRig): LevelData {
  const colliders: AABB[] = [];
  const minimap: LevelData["minimap"] = [];
  const buckets: Bucket = new Map();
  const group = new THREE.Group();
  const nav: THREE.Vector3[] = [];
  const covers: CoverPoint[] = [];
  const spawns: SpawnPoint[] = [];
  const interacts: Interactable[] = [];

  const boxGeo = new THREE.BoxGeometry(1, 1, 1);
  const cylGeo = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
  const poleGeo = new THREE.CylinderGeometry(0.07, 0.09, 1, 8);

  // Ground slabs
  boxAt(buckets, colliders, minimap, "asphalt", 0, -0.15, 40, 72, 0.3, 110, 0, false);
  colliders.push(makeAABB(0, -0.5, 40, 200, 1, 200)); // infinite-ish floor via large slab already y=0 mover

  // Water west
  addMesh(buckets, "water", boxGeo, new THREE.Vector3(-46, -0.35, 40), 0, new THREE.Vector3(28, 0.4, 120));
  // Seawall
  boxAt(buckets, colliders, minimap, "concrete", -30.5, 0.7, 40, 1.4, 1.4, 110);
  // Hazard lip
  boxAt(buckets, colliders, minimap, "hazard", -29.6, 0.12, 40, 0.4, 0.12, 110, 0, false);

  // East fence wall
  boxAt(buckets, colliders, minimap, "gray", 32, 1.6, 40, 0.25, 3.2, 110);

  // ——— SPAWN DOCK / INFIL ———
  boxAt(buckets, colliders, minimap, "concrete", 0, 0.15, 4, 18, 0.3, 12, 0, false);
  boxAt(buckets, colliders, minimap, "diamond", 0, 0.18, 4, 8, 0.04, 8, 0, false);

  // Light poles along dock
  const lampPosts = (x: number, z: number, kind: "sodium" | "led" = "sodium") => {
    addMesh(buckets, "gray", poleGeo, new THREE.Vector3(x, 3.1, z), 0, new THREE.Vector3(1, 6.2, 1));
    addMesh(buckets, kind === "led" ? "emissiveLed" : "emissiveSodium", boxGeo, new THREE.Vector3(x, 6.25, z), 0, new THREE.Vector3(0.55, 0.25, 0.55));
    lights.addLamp(x, 6.05, z, kind);
  };
  for (let z = 6; z <= 88; z += 12) {
    lampPosts(-26, z, z % 24 === 6 ? "led" : "sodium");
    lampPosts(26, z, "sodium");
  }

  // ——— CONTAINER YARD ———
  type CSpec = { x: number; z: number; rot?: number; col: "orange" | "teal" | "gray"; stack?: number; open?: boolean };
  const containers: CSpec[] = [
    { x: -16, z: 20, col: "orange", stack: 2 },
    { x: -16, z: 28, col: "teal" },
    { x: -8, z: 22, col: "gray", rot: Math.PI / 2 },
    { x: 2, z: 26, col: "orange", stack: 2 },
    { x: 12, z: 20, col: "teal" },
    { x: 12, z: 28, col: "orange" },
    { x: 20, z: 24, col: "gray", stack: 2 },
    { x: -20, z: 36, col: "teal", stack: 2 },
    { x: -10, z: 38, col: "orange" },
    { x: 0, z: 36, col: "gray" },
    { x: 10, z: 38, col: "teal", stack: 2 },
    { x: 20, z: 36, col: "orange" },
    { x: -14, z: 46, col: "gray", rot: Math.PI / 2 },
    { x: 6, z: 46, col: "orange", rot: Math.PI / 2, stack: 2 },
    { x: 18, z: 48, col: "teal" },
    { x: -22, z: 52, col: "orange" },
  ];

  const C_L = 6.05,
    C_W = 2.44,
    C_H = 2.59;
  for (const c of containers) {
    const n = c.stack ?? 1;
    for (let i = 0; i < n; i++) {
      const y = C_H * 0.5 + i * C_H;
      boxAt(buckets, colliders, minimap, c.col, c.x, y, c.z, C_L, C_H, C_W, c.rot ?? 0);
    }
    covers.push({ pos: new THREE.Vector3(c.x + 2.2, 0, c.z + 2), yaw: 0 });
    covers.push({ pos: new THREE.Vector3(c.x - 2.2, 0, c.z - 2), yaw: Math.PI });
    nav.push(new THREE.Vector3(c.x + 4, 0, c.z));
    nav.push(new THREE.Vector3(c.x, 0, c.z + 4));
  }

  // Crates / barrels clusters
  const scatter = [
    [-6, 18],
    [8, 18],
    [-4, 32],
    [16, 32],
    [-18, 44],
    [4, 52],
    [22, 54],
    [-8, 58],
  ] as const;
  for (const [x, z] of scatter) {
    for (let i = 0; i < 4; i++) {
      const ox = (i % 2) * 0.95;
      const oz = Math.floor(i / 2) * 0.95;
      addMesh(buckets, "tan", boxGeo, new THREE.Vector3(x + ox, 0.45, z + oz), 0.1 * i, new THREE.Vector3(0.85, 0.9, 0.75));
      colliders.push(makeAABB(x + ox, 0.45, z + oz, 0.85, 0.9, 0.75));
    }
    addMesh(buckets, "rust", cylGeo, new THREE.Vector3(x + 2.1, 0.55, z + 0.4), 0, new THREE.Vector3(0.72, 1.1, 0.72));
    colliders.push(makeAABB(x + 2.1, 0.55, z + 0.4, 0.72, 1.1, 0.72));
    nav.push(new THREE.Vector3(x + 1, 0, z + 3));
  }

  // Puddles
  const puddles = [
    [0, 16],
    [-8, 30],
    [14, 34],
    [-12, 50],
    [8, 62],
    [0, 78],
    [-20, 22],
  ];
  for (const [x, z] of puddles) {
    const g = new THREE.CircleGeometry(1.8 + Math.random() * 1.4, 20);
    g.rotateX(-Math.PI / 2);
    addMesh(buckets, "puddle", g, new THREE.Vector3(x, 0.03, z));
  }

  // ——— PROCESSING PLANT ———
  // Main hall
  const hallZ = 68;
  boxAt(buckets, colliders, minimap, "concrete", 0, 4.5, hallZ, 28, 0.4, 22); // roof
  boxAt(buckets, colliders, minimap, "concrete", -14, 2.2, hallZ, 0.45, 4.4, 22); // walls
  boxAt(buckets, colliders, minimap, "concrete", 14, 2.2, hallZ, 0.45, 4.4, 22);
  boxAt(buckets, colliders, minimap, "concrete", 0, 2.2, 79, 28, 4.4, 0.45);
  // south wall with doorway
  boxAt(buckets, colliders, minimap, "concrete", -9, 2.2, 57, 10, 4.4, 0.45);
  boxAt(buckets, colliders, minimap, "concrete", 9, 2.2, 57, 10, 4.4, 0.45);
  boxAt(buckets, colliders, minimap, "concrete", 0, 3.6, 57, 8, 1.6, 0.45);
  // interior floor
  boxAt(buckets, colliders, minimap, "diamond", 0, 0.02, hallZ, 26, 0.04, 20, 0, false);

  // Interior columns + tanks
  for (const x of [-8, 8]) {
    addMesh(buckets, "rust", cylGeo, new THREE.Vector3(x, 2.1, 64), 0, new THREE.Vector3(2.4, 4.2, 2.4));
    colliders.push(makeAABB(x, 2.1, 64, 2.2, 4.2, 2.2));
    addMesh(buckets, "gray", cylGeo, new THREE.Vector3(x, 2.1, 73), 0, new THREE.Vector3(1.8, 4.2, 1.8));
    colliders.push(makeAABB(x, 2.1, 73, 1.6, 4.2, 1.6));
  }
  // Pipe racks
  for (let i = 0; i < 5; i++) {
    const z = 60 + i * 4;
    addMesh(buckets, "gray", cylGeo, new THREE.Vector3(0, 3.4, z), Math.PI / 2, new THREE.Vector3(0.35, 22, 0.35));
    addMesh(buckets, "rust", cylGeo, new THREE.Vector3(0, 3.85, z), Math.PI / 2, new THREE.Vector3(0.22, 22, 0.22));
  }
  // Interior lights
  for (const [x, z] of [
    [-6, 62],
    [6, 62],
    [-6, 72],
    [6, 72],
  ] as const) {
    addMesh(buckets, "emissiveLed", boxGeo, new THREE.Vector3(x, 4.15, z), 0, new THREE.Vector3(1.6, 0.12, 0.4));
    lights.addLamp(x, 3.9, z, "led");
  }

  // Catwalk
  boxAt(buckets, colliders, minimap, "diamond", 11, 2.55, 68, 3.2, 0.12, 18);
  boxAt(buckets, colliders, minimap, "gray", 9.5, 3.05, 68, 0.08, 1.1, 18, 0, false);
  boxAt(buckets, colliders, minimap, "gray", 12.5, 3.05, 68, 0.08, 1.1, 18, 0, false);
  // stairs to catwalk
  for (let i = 0; i < 8; i++) {
    boxAt(buckets, colliders, minimap, "diamond", 11, 0.16 + i * 0.3, 57.8 + i * 0.42, 3.2, 0.12, 0.5);
  }

  // ——— CONTROL TOWER + HELIPAD ———
  boxAt(buckets, colliders, minimap, "concrete", 0, 4, 90, 10, 8, 10);
  boxAt(buckets, colliders, minimap, "plaster", 0, 4, 90, 9.2, 7.6, 9.2, 0, false);
  // doorway
  // carve by not covering south opening - use two pillars
  // actually solid tower - punch opening with negative space: split south wall conceptually by using a hollow: we already filled solid. Rebuild tower as walls.
  // Remove solid tower approach: the solid box blocks interior. Replace: don't use the solid 10x8x10 as collider-only shell.

  // Control interior floor at y=0 still accessible? Tower is solid - BAD.
  // I'll add an annex instead of hollow tower: external stairs to helipad roof.

  for (let i = 0; i < 14; i++) {
    boxAt(buckets, colliders, minimap, "diamond", 6.8, 0.18 + i * 0.55, 84 + Math.min(i, 8) * 0.15, 2.4, 0.14, 1.1);
  }
  // Helipad
  boxAt(buckets, colliders, minimap, "concrete", 0, 8.15, 90, 16, 0.3, 16);
  {
    const ring = new THREE.Mesh(new THREE.RingGeometry(3.2, 3.6, 40), mats.hazard);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(0, 8.34, 90);
    ring.receiveShadow = true;
    group.add(ring);
  }
  // H mark
  boxAt(buckets, colliders, minimap, "hazard", 0, 8.34, 90, 2.4, 0.02, 0.35, 0, false);
  boxAt(buckets, colliders, minimap, "hazard", -1.05, 8.34, 90, 0.35, 0.02, 2.4, 0, false);
  boxAt(buckets, colliders, minimap, "hazard", 1.05, 8.34, 90, 0.35, 0.02, 2.4, 0, false);
  lights.addLamp(0, 10.5, 90, "led");
  addMesh(buckets, "emissiveLed", boxGeo, new THREE.Vector3(0, 10.4, 90), 0, new THREE.Vector3(0.4, 0.2, 0.4));
  lights.addLamp(-6, 9.2, 84, "sodium");
  lights.addLamp(6, 9.2, 84, "sodium");

  // Beacon interact on helipad + plant point in control annex
  const beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.35, 0.7, 12), mats.black);
  beacon.position.set(0, 8.65, 90);
  group.add(beacon);
  const beaconLight = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 8), mats.emissiveRed);
  beaconLight.position.set(0, 9.1, 90);
  group.add(beaconLight);
  interacts.push({
    id: "beacon",
    pos: new THREE.Vector3(0, 8.5, 90),
    radius: 2.2,
    label: "PLANT TARGETING BEACON",
    done: false,
    mesh: beacon,
  });

  // Extract volume
  const extract = new THREE.Vector3(0, 8.4, 90);

  // Side warehouse east
  boxAt(buckets, colliders, minimap, "gray", 24, 2.4, 62, 10, 4.8, 16);
  boxAt(buckets, colliders, minimap, "orange", 24, 2.2, 62, 9.6, 4.4, 15.6, 0, false);
  lights.addLamp(20, 4.5, 62, "sodium");

  // Red strobes on plant roof
  lights.addLamp(-12, 5.2, 68, "red");
  lights.addLamp(12, 5.2, 68, "red");
  addMesh(buckets, "emissiveRed", boxGeo, new THREE.Vector3(-12, 5.1, 68), 0, new THREE.Vector3(0.2, 0.2, 0.2));
  addMesh(buckets, "emissiveRed", boxGeo, new THREE.Vector3(12, 5.1, 68), 0, new THREE.Vector3(0.2, 0.2, 0.2));

  // Signage bars (no text)
  addMesh(buckets, "emissiveSodium", boxGeo, new THREE.Vector3(0, 4.8, 56.6), 0, new THREE.Vector3(4.5, 0.35, 0.08));
  addMesh(buckets, "emissiveLed", boxGeo, new THREE.Vector3(-14.1, 3.2, 20), 0, new THREE.Vector3(0.08, 1.2, 3.2));

  // Nav graph corridors
  for (let z = 10; z <= 95; z += 6) {
    nav.push(new THREE.Vector3(0, 0, z));
    nav.push(new THREE.Vector3(-22, 0, z));
    nav.push(new THREE.Vector3(22, 0, z));
  }
  nav.push(new THREE.Vector3(11, 2.55, 68));
  nav.push(new THREE.Vector3(0, 8.4, 90));
  nav.push(new THREE.Vector3(6, 4, 86));

  // Near-spawn cover so the opening isn't a shooting gallery
  boxAt(buckets, colliders, minimap, "tan", -3.2, 0.55, 10, 1.4, 1.1, 1.2);
  boxAt(buckets, colliders, minimap, "tan", -1.8, 0.45, 10.4, 1.1, 0.9, 1.0);
  addMesh(buckets, "rust", cylGeo, new THREE.Vector3(3.4, 0.55, 11), 0, new THREE.Vector3(0.7, 1.1, 0.7));
  colliders.push(makeAABB(3.4, 0.55, 11, 0.7, 1.1, 0.7));

  // First three stand in the OPEN lane — visible from infil. Rest deeper.
  const spawnList: SpawnPoint[] = [
    { pos: new THREE.Vector3(0, 0, 18), yaw: 1.35, role: "guard" },
    { pos: new THREE.Vector3(-4.2, 0, 23), yaw: 0.6, role: "patrol" },
    { pos: new THREE.Vector3(5.8, 0, 26), yaw: -0.5, role: "patrol" },
    { pos: new THREE.Vector3(-12, 0, 40), yaw: 0.8, role: "guard" },
    { pos: new THREE.Vector3(14, 0, 42), yaw: -0.7, role: "guard" },
    { pos: new THREE.Vector3(-18, 0, 50), yaw: 1.1, role: "patrol" },
    { pos: new THREE.Vector3(16, 0, 52), yaw: -1.0, role: "patrol" },
    { pos: new THREE.Vector3(0, 0, 54), yaw: 0.1, role: "roamer" },
    { pos: new THREE.Vector3(-10, 0, 62), yaw: 0.4, role: "guard" },
    { pos: new THREE.Vector3(10, 0, 62), yaw: -0.4, role: "guard" },
    { pos: new THREE.Vector3(-6, 0, 72), yaw: 2.2, role: "patrol" },
    { pos: new THREE.Vector3(6, 0, 74), yaw: -2, role: "patrol" },
    { pos: new THREE.Vector3(11, 2.55, 74), yaw: 0, role: "guard" },
    { pos: new THREE.Vector3(18, 0, 70), yaw: -0.8, role: "patrol" },
    { pos: new THREE.Vector3(8, 8.4, 88), yaw: 0, role: "guard" },
  ];
  for (const s of spawnList) {
    for (let k = 0; k < 10; k++) {
      let hit = false;
      for (const b of colliders) {
        if (b.max.y < 0.9) continue;
        if (s.pos.x < b.min.x - 0.7 || s.pos.x > b.max.x + 0.7) continue;
        if (s.pos.z < b.min.z - 0.7 || s.pos.z > b.max.z + 0.7) continue;
        const dl = s.pos.x - (b.min.x - 0.7);
        const dr = b.max.x + 0.7 - s.pos.x;
        const db = s.pos.z - (b.min.z - 0.7);
        const df = b.max.z + 0.7 - s.pos.z;
        const m = Math.min(dl, dr, db, df);
        if (m === dl) s.pos.x = b.min.x - 0.9;
        else if (m === dr) s.pos.x = b.max.x + 0.9;
        else if (m === db) s.pos.z = b.min.z - 0.9;
        else s.pos.z = b.max.z + 0.9;
        hit = true;
        break;
      }
      if (!hit) break;
    }
    spawns.push(s);
  }

  // Merge buckets into meshes
  for (const [key, geos] of buckets) {
    const mat = mats[key];
    if (!mat) continue;
    const usable = geos.filter((g) => g.getAttribute("position"));
    if (!usable.length) continue;
    let merged: THREE.BufferGeometry;
    try {
      merged = mergeGeometries(usable, false)!;
    } catch {
      for (const g of usable) {
        const mesh = new THREE.Mesh(g, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      continue;
    }
    if (!merged) continue;
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = key !== "water" && key !== "puddle";
    mesh.receiveShadow = true;
    if (key === "puddle" || key === "water" || key.startsWith("emissive")) {
      mesh.castShadow = false;
    }
    group.add(mesh);
    for (const g of usable) g.dispose();
  }

  // Fog volume feel via extra dark planes far away? skip

  return {
    colliders,
    nav,
    covers,
    spawns,
    interacts,
    playerStart: new THREE.Vector3(0, 0, 4),
    extract,
    group,
    minimap,
  };
}
